// Single source of truth para classificacao de ``volatility`` de
// inputs V3 no frontend. Centraliza checks como "isPermanente?",
// "isReusavel?", "elegivel para multi-mes?" — substitui dezenas de
// ``volatility === "fixed"`` espalhados pela UI.
//
// Rev. 2026-05-26 — desambiguacao do antigo ``"fixed"`` (ver
// backend/period_window.py e migration 0034).
// Rev. 2026-05-28 — REMOCAO de ``"semi_fixed"`` (ver migration 0035):
// a volatility era redundante com as flags ``reuse_allowed`` /
// ``reuse_auto`` no nivel do input. Inputs que antes eram
// ``semi_fixed`` foram migrados para ``monthly`` + ``reuse_auto=true``;
// o comportamento "verde sem upload mensal" e o marker "Repeat" agora
// sao derivados exclusivamente das flags reuse.
//
// Conceitos:
//
//   - ``monthly``        : 1 entrega por mes. Cada mes independente.
//                          UI mostra calendario completo com pip por
//                          mes; multi-mes faz sentido (subir N
//                          arquivos diferentes para N meses). Pode
//                          ter reuso manual/automatico ligado via
//                          flags ``reuse_allowed``/``reuse_auto``
//                          (substituiu o antigo ``semi_fixed``).
//   - ``carry_forward``  : 1 entrega vale "do mes em diante" ate
//                          alguem subir o proximo. Cria vigencias
//                          sequenciais. Multi-mes aplica (subir o
//                          MESMO arquivo em N meses cria N
//                          vigencias).
//   - ``singleton``      : 1 unico arquivo cobre tudo
//                          (passado/presente/futuro). Novo upload
//                          retroage automaticamente. NAO tem
//                          calendario por mes — multi-mes nao se
//                          aplica.
//   - ``yearly``         : 1 entrega por ano fiscal. NAO tem
//                          identidade mensal — multi-mes nao se
//                          aplica.
//
// Legado: o valor ``"fixed"`` foi renomeado para ``"singleton"``
// pelo backend (migration 0034). Aceitamos ``"fixed"`` aqui como
// alias defensivo para snapshots em cache / estados intermediarios
// que ainda possam carregar o valor antigo. **Nao introduzir novo
// codigo com ``"fixed"``** — sempre usar ``"singleton"``.

const LEGACY_FIXED_AS_SINGLETON = (v) => v === "fixed" || v === "singleton";

export function isMonthly(volatility) {
  return volatility === "monthly";
}

export function isCarryForward(volatility) {
  return volatility === "carry_forward";
}

export function isSingleton(volatility) {
  return LEGACY_FIXED_AS_SINGLETON(volatility);
}

export function isYearly(volatility) {
  return volatility === "yearly";
}

// "Permanente": uma unica entrega cobre TODOS os meses do escopo
// (passado/futuro para singleton, FY inteiro para yearly). NAO
// inclui ``carry_forward`` porque la cada vigencia tem inicio
// explicito; UI deve mostrar historico.
export function isPermanent(volatility) {
  return isSingleton(volatility) || isYearly(volatility);
}

// "Reusavel" (visual): uma entrega cobre VARIOS meses, mesmo que
// nao seja "permanente" no sentido estrito. Usado para o badge
// "reutilizavel" do Overview e para o icone de marcador do
// InputBox.
//
// Rev. 2026-05-28 — ``monthly`` com ``reuse_auto=true`` (ex-
// ``semi_fixed``) tambem e' considerado reusavel; a verificacao
// requer o input completo (volatility + flags), entao use
// ``isReusableInput`` quando tiver acesso ao objeto. A funcao
// abaixo cobre apenas os casos onde a volatility sozinha ja
// indica reusabilidade.
export function isReusable(volatility) {
  return (
    isSingleton(volatility) ||
    isYearly(volatility) ||
    isCarryForward(volatility)
  );
}

// Versao input-aware de ``isReusable``: inclui o caso ``monthly``
// + ``reuse_auto=true`` (ex-``semi_fixed``). Aceita um objeto com
// ``volatility``, ``reuse_allowed`` e ``reuse_auto``.
export function isReusableInput(input) {
  if (!input) return false;
  const v = input.volatility;
  if (isReusable(v)) return true;
  return (
    isMonthly(v) && (Boolean(input.reuse_auto) || Boolean(input.reuse_allowed))
  );
}

// Elegivel para o modal "Enviar multiplos meses": vale apenas
// para inputs cuja unidade temporal e o MES E onde cada upload
// representa um conteudo potencialmente DIFERENTE por mes.
// - ``monthly``: trivial — cada mes pode receber arquivo
//   diferente. Incluindo casos com ``reuse_auto=true`` (cada
//   slot mensal continua sendo independente; o auto-reuse
//   apenas copia a ultima entrega quando nao ha upload no mes).
// - ``singleton`` / ``yearly`` / ``carry_forward``: NAO aplica.
//   Em todos esses casos UMA entrega ja cobre multiplos meses
//   por design — singleton cobre tudo, yearly cobre o FY,
//   carry_forward cobre do mes do upload em diante. Subir um
//   arquivo em N meses simultaneamente nao tem semantica
//   pratica.
export function isMultiMonthEligible(volatility) {
  return isMonthly(volatility);
}

// "Reuso manual aplicavel": o toggle ``reuse_allowed`` /
// ``reuse_auto`` da AboutPane so faz sentido para inputs onde o
// cliente pode repetir manualmente o arquivo entre meses. NAO
// se aplica a singleton/yearly (a propria entrega ja cobre todos
// os meses do escopo) nem a carry_forward (o sistema fecha a
// vigencia anterior automaticamente — nao precisa "repetir").
export function isManualReuseApplicable(volatility) {
  return isMonthly(volatility);
}
